import { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';

import { toast } from "react-toastify";
import { Dropdown } from "primereact/dropdown";
import { Col, Row, Form, Button, Spinner } from "react-bootstrap";
import Error from "../../../../../../../components/Error";

import { getAllBanks, newAccountBank, updateAccountBank } from "../../../../../../../services/manager/partner/withdrawl.service";
import UsuarioContext from "../../../../../../../../context/userContext/context";

const validationSchema = Yup.object({
  account_name: Yup.string().required("Este campo é obrigatório"),
  financialInstitution: Yup.string().required("Este campo é obrigatório"),
  agency: Yup.string().required("Este campo é obrigatório"),
  account: Yup.string().required("Este campo é obrigatório"),
  account_type: Yup.string().required("Este campo é obrigatório"),
  pix_key: Yup.string(),
  save_account: Yup.boolean()
}).required();

export const FormAccount = (props: any): JSX.Element => {
  const { user } = useContext(UsuarioContext)
  const { t: translation } = useTranslation();
  const { register, handleSubmit, setValue, formState: { errors } } = useForm({ resolver: yupResolver(validationSchema) });
  const { account, handleCancelEdit } = props;

  const [loading, setLoading] = useState<boolean>(true);
  const [loadingSubmit, setLoadingSubmit] = useState<boolean>(false);
  const [financialInstitutionSelected, setFinancialInstitutionSelected] = useState<string>("");
  const [accountTypeSelected, setAccountTypeSelected] = useState<string>("");
  const [financialInstitutionOptions, setFinancialInstitutionOptions] = useState<any>([]);

  const accountTypeOptions = [
    {label: 'Poupança', value: 1},
    {label: 'Corrente', value: 2},
  ];

  const onSubmitForm = (formData: any) => {
    setLoadingSubmit(true)
    let obj = {
      name: formData.account_name,
      agency: formData.agency,
      account: formData.account,
      pix: formData.pix_key,
      account_type: formData.account_type,
      profile: account ? account.profile : user.id,
      bank: formData.financialInstitution,
    }

    if (account) {
      updateAccountBank(account.id, obj).then(() => {
        toast.success('Conta alterada com sucesso!')
        handleCancelEdit(false)
        setLoadingSubmit(false)
      }).catch((err: any) => {
        toast.error('Erro ao alterar sua conta bancária. Tente novamente.')
        setLoadingSubmit(false)
      })
    } else {
      newAccountBank(obj).then(() => {
        toast.success('Conta adicionada com sucesso!')
        handleCancelEdit(false)
        setLoadingSubmit(false)
      }).catch((err: any) => {
        toast.error('Erro ao criar sua conta bancária. Tente novamente.')
        setLoadingSubmit(false)
      })
    }
  };

  useEffect(() => {
    setLoading(true)
    getAllBanks().then((banks) => {
      let options: any = []
      banks.map((bank: any) => {
        var pad = "000"
        var ans = pad.substring(0, pad.length - bank.number.toString().length) + bank.number.toString()
        let obj = {label: ans + ' - ' + bank.short_name, value: bank.id}
        options.push(obj)
      })
      setFinancialInstitutionOptions(options)
      setLoading(false)
    })

    if (account) {
      setValue('account_name', account.name);
      setValue('agency', account.agency);
      setValue('account', account.account);
      setValue('pix_key', account.pix);
      setValue('account_type', account.account_type);
      setAccountTypeSelected(account.account_type)
      setValue('financialInstitution', account.bank);
      setFinancialInstitutionSelected(account.bank);
    }
  }, [])

  if (loading) {
    return (
      <div className="loading-container mt-5">
        <Spinner animation="border" variant="primary" />
      </div>
    )
  }

  return (
    <Form onSubmit={handleSubmit(onSubmitForm)}>
      <Row>
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label className="text-darker-grey">{translation('manager.withdrawal.accountName')}:</Form.Label>
            <Form.Control type="text" placeholder={translation('manager.withdrawal.accountNamePlaceholder')} {...register("account_name", { required: true })}/>
            {errors.account_name ? (<div className="w-100"> <Error>{errors.account_name?.message}</Error> </div>) : ""}
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label className="text-darker-grey">{translation('manager.withdrawal.agency')}:</Form.Label>
            <Form.Control type="text" placeholder={translation('manager.withdrawal.agencyPlaceholder')} {...register("agency", { required: true })}/>
            {errors.agency ? (<div className="w-100"> <Error>{errors.agency?.message}</Error> </div>) : ""}
          </Form.Group>

          <Form.Group className="mb-4 mt-3">
            <Form.Label className="text-darker-grey">{translation('manager.withdrawal.accountType')}:</Form.Label><br/>
            <Dropdown
              className="w-100 select-form-default"
              value={accountTypeSelected}
              options={accountTypeOptions}
              onChange={(e) => {
                setValue('account_type', e.value);
                setAccountTypeSelected(e.value)
              }} 
              placeholder="Selecione seu tipo de conta"/>
            {errors.account_type ? (<div className="w-100"> <Error>{errors.account_type?.message}</Error> </div>) : ""}
          </Form.Group>
        </Col>

        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label className="text-darker-grey">{translation('manager.withdrawal.financialInstitution')}:</Form.Label><br/>
            <Dropdown
              className="w-100 select-form-default"
              filter
              filterBy="label"
              value={financialInstitutionSelected}
              options={financialInstitutionOptions} 
              onChange={(e) => {
                  setValue('financialInstitution', e.value);
                  setFinancialInstitutionSelected(e.value)
              }} 
              placeholder="Seleciona sua instituição financeira"/>
            {errors.financialInstitution ? (<div className="w-100"> <Error>{errors.financialInstitution?.message}</Error> </div>) : ""}
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label className="text-darker-grey">{translation('manager.withdrawal.accountDigit')}:</Form.Label>
            <Form.Control type="text" placeholder={translation('manager.withdrawal.accountDigitPlaceholder')} {...register("account", { required: true })}/>
            {errors.account ? (<div className="w-100"> <Error>{errors.account?.message}</Error> </div>) : ""}
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label className="text-darker-grey">{translation('manager.withdrawal.pixKey')} (Opcional):</Form.Label>
            <Form.Control type="text" placeholder={translation('manager.withdrawal.pixKeyPlaceholder')} {...register("pix_key")}/>
          </Form.Group>
        </Col>

        <Col md={12}>
          <Form.Check 
            className="mb-3" 
            type="checkbox" 
            id="sendMail"
            {...register("save_account")}
            label={translation('manager.withdrawal.saveAccount')}
          />
        </Col>

        <Col md={12} className="mt-3">
          <Button variant="outline-primary" type="button" className="mb-2" disabled={loadingSubmit} onClick={() => handleCancelEdit(false)}>{translation('cancel')}</Button>
          <Button type="submit" className="ml-2 mr-2 mb-2" disabled={loadingSubmit}>{translation('save')}</Button>
          {
            loadingSubmit &&
            <Spinner animation="border" variant="primary" style={{ width: '1.5rem', height: '1.5rem', borderWidth: '0.20em', borderRightColor: 'transparent' }}/>
          }
        </Col>
      </Row>
    </Form>
  )
}